import { Injectable, inject, signal } from '@angular/core';

import {
  BranchState,
  CompanyNode,
} from 'src/app/app-common/types/company-tree';
import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';

export const COMPANY_TREE_CACHE_TTL_MS = 5 * 60 * 1000;

/**
 * How a snapshot came out of `open`: usable as it is, usable but worth
 * refreshing behind the panel, or gone.
 */
export type CompanyTreeSnapshot = 'fresh' | 'stale' | 'empty';

/**
 * Holds the loaded hierarchy for the page session so reopening the company
 * selector paints from memory instead of from the network. The panel and its
 * `CompanyTreeService` are created and destroyed with the overlay; this is the
 * only piece that outlives them.
 */
@Injectable({
  providedIn: 'root',
})
export class CompanyTreeCacheService {
  private readonly broadcaster = inject(BroadcasterService);

  readonly nodes = signal(new Map<string, CompanyNode>());
  readonly branches = signal(new Map<string, BranchState>());
  readonly knownLeaves = signal(new Set<string>());

  private ownerId: string | null = null;
  private loadedAt: number | null = null;
  private refreshing = false;

  constructor() {
    this.broadcaster.on('risevision.user.signedOut', () => this.invalidate());
    this.broadcaster.on('risevision.company.updated', () => this.invalidate());
    // `risevision.company.selectedCompanyChanged` is deliberately not a
    // trigger. The hierarchy is rooted at the user's own company and never
    // depends on the selection, and that event fires on every switch —
    // including every one made from this panel, which would leave nothing to
    // remember.
  }

  /**
   * Prepares the snapshot for one panel opening, dropping any branch that did
   * not finish loading so it is retried rather than re-rendered as a stale
   * error or a spinner that can never settle.
   */
  open(userCompanyId: string): CompanyTreeSnapshot {
    if (
      !userCompanyId ||
      this.ownerId !== userCompanyId ||
      this.loadedAt === null
    ) {
      this.clear();
      this.ownerId = userCompanyId || null;
      return 'empty';
    }

    this.dropUnloadedBranches();

    if (this.branches().get(userCompanyId)?.status !== 'loaded') {
      this.clear();
      this.ownerId = userCompanyId;
      return 'empty';
    }

    return Date.now() - this.loadedAt > COMPANY_TREE_CACHE_TTL_MS
      ? 'stale'
      : 'fresh';
  }

  owns(userCompanyId: string): boolean {
    return !!userCompanyId && this.ownerId === userCompanyId;
  }

  /** Stamps the snapshot as loaded. Only ever called after a real fetch. */
  markLoaded(): void {
    this.loadedAt = Date.now();
  }

  beginRefresh(): boolean {
    if (this.refreshing) {
      return false;
    }

    this.refreshing = true;
    return true;
  }

  endRefresh(ok: boolean): void {
    this.refreshing = false;

    // A refresh that outlived its snapshot must not revive it.
    if (ok && this.loadedAt !== null) {
      this.loadedAt = Date.now();
    }
  }

  invalidate(): void {
    this.clear();
    this.ownerId = null;
  }

  private clear(): void {
    this.nodes.set(new Map());
    this.branches.set(new Map());
    this.knownLeaves.set(new Set());
    this.loadedAt = null;
    this.refreshing = false;
  }

  private dropUnloadedBranches(): void {
    const branches = this.branches();
    const loaded = new Map<string, BranchState>();
    for (const [id, branch] of branches) {
      if (branch.status === 'loaded') {
        loaded.set(id, branch);
      }
    }

    if (loaded.size !== branches.size) {
      this.branches.set(loaded);
    }
  }
}
